/**
 * "Ask the coach": the conversation between the owning expert and the rubric coach.
 *
 * The coach questions and challenges the rubric; it never writes it. Experts chat, engineers read
 * the same thread and can download it as Markdown. Replies arrive
 * asynchronously: while `thread.status` is `in_progress` the panel polls `?after=<last seq>`.
 *
 * Messages are kept oldest -> newest and merged by `seq`, so an optimistic append, a poll and a
 * "load earlier" page never produce duplicates.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { api, ApiError } from '@/api/client'
import { postWhenCoachReady, coachProgressLabel, type CoachProgress } from '@/api/coach'
import { Markdown } from './Markdown'
import { Section } from './Section'
import { CoachAttachments, CoachAttachmentLink } from './CoachAttachments'
import { MessageSendButton } from './MessageComposer'
import { validate } from '@/lib/schema'
import { fmtDate } from '@/lib/format'
import type { CoachAttachment, CoachDraft, CoachMessage, CoachPage, CoachSend, CoachThread, Role } from '@/lib/types'

const PAGE_SIZE = 50
const POLL_MS = 250   // the server already holds each poll while a turn runs
const MAX_CHARS = 4000
const UNAVAILABLE = 'The coach is not available in this environment.'
const EXAMPLES = [
  'Is my rubric precise enough for a verifier?',
  'Which of my criteria could an AI game?',
  'What would make this criterion fail on its own?',
]

interface Props {
  taskId: string
  /** Static introduction messages; no fetch, upload, or agent send is allowed. */
  exampleMessages?: CoachMessage[]
  conversationUrl?: string
  /** Refresh an externally started turn without remounting the conversation. */
  refreshToken?: number
  /** Activity history shows saved conversations without a composer. */
  readOnly?: boolean
  /** Only the owning author’s task Coach tab shows onboarding suggestions. */
  showIntro?: boolean
  /** Full draft review owns the initial send; follow-up is available after feedback arrives. */
  awaitingReview?: boolean
  onThreadChange?: (thread: CoachThread | null) => void
  role: Role
  /** Called at send time; the unsaved editor state travels with the question. */
  draft?: () => CoachDraft
  /** Inside the draft editor: no section heading, shorter log. */
  compact?: boolean
}

function merge(existing: CoachMessage[], incoming: CoachMessage[]): CoachMessage[] {
  const bySeq = new Map(existing.map((m) => [m.seq, m]))
  for (const m of incoming) bySeq.set(m.seq, m)
  return [...bySeq.values()].sort((a, b) => (a.seq < b.seq ? -1 : a.seq > b.seq ? 1 : 0))
}

/** Drop `undefined` values (they would fail the schema's `additionalProperties` checks) and empty parts. */
function cleanDraft(d: CoachDraft): CoachDraft | undefined {
  const clean = JSON.parse(JSON.stringify(d)) as CoachDraft
  if (clean.prompt !== undefined && !clean.prompt.trim()) delete clean.prompt
  if (clean.rubric && clean.rubric.length === 0) delete clean.rubric
  return Object.keys(clean).length ? clean : undefined
}

export function CoachPanel({ taskId, role, draft, compact = false, conversationUrl, awaitingReview = false, onThreadChange, refreshToken = 0, readOnly = false, showIntro = false, exampleMessages }: Props) {
  const engineer = role === 'engineer'
  const [page, setPage] = useState<CoachPage | null>(null)
  const [messages, setMessages] = useState<CoachMessage[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState<CoachProgress | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [attachments, setAttachments] = useState<CoachAttachment[]>([])
  const [uploadsBlocked, setUploadsBlocked] = useState(false)
  const [attachmentKey, setAttachmentKey] = useState(0)
  const [retrySync, setRetrySync] = useState(0)
  const lastSend = useRef<{ signature: string; id: string }>()
  const [actionBusy, setActionBusy] = useState<'download' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const logRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const lastSeqRef = useRef<string | undefined>(undefined)
  const readyRef = useRef(false)          // first page loaded and the coach is enabled
  const pollingRef = useRef(false)        // one request in flight at a time
  const prependRef = useRef<number | null>(null)  // scrollHeight before older messages were prepended

  const visible = messages
  const lastSeq = messages.length ? messages[messages.length - 1].seq : undefined
  lastSeqRef.current = lastSeq
  const thread = page?.thread ?? null
  const inProgress = thread?.status === 'in_progress'
  useEffect(() => { if (page) onThreadChange?.(thread) }, [page, thread, onThreadChange])
  const url = conversationUrl ?? `/tasks/${taskId}/coach`

  useEffect(() => {
    setText(''); setAttachments([]); setUploadsBlocked(false); lastSend.current = undefined
  }, [taskId])

  // First page, newest-first from the API, shown oldest -> newest.
  useEffect(() => {
    let cancelled = false
    readyRef.current = false
    setPage(null); setMessages([]); setCursor(null); setLoadError(null)
    if (exampleMessages) {
      setPage({ enabled: true, permissions: { can_chat: true, can_export: false }, thread: null, messages: exampleMessages, cursor: null })
      setMessages(exampleMessages)
      return
    }
    api.get<CoachPage>(url, { limit: PAGE_SIZE }).then((p) => {
      if (cancelled) return
      setPage(p); setMessages([...p.messages].reverse()); setCursor(p.cursor)
      readyRef.current = p.enabled
    }).catch((e) => {
      if (cancelled) return
      // A disabled coach answers 200 with enabled:false; any error here is transient and worth retrying.
      setLoadError((e as Error).message || UNAVAILABLE)
    })
    return () => { cancelled = true }
  }, [url, reloadKey, exampleMessages])

  const poll = useCallback(async (wait = false) => {
    if (!readyRef.current || pollingRef.current) return
    pollingRef.current = true
    try {
      const after = lastSeqRef.current
      // While a turn runs the server holds the request for a few seconds and relays the agent's live steps.
      const p = await api.get<CoachPage>(url, { ...(after ? { after } : { limit: PAGE_SIZE }), ...(wait ? { wait: 1 } : {}) })
      setPage((prev) => (prev ? { ...prev, enabled: p.enabled, permissions: p.permissions, thread: p.thread, pending_send: p.pending_send } : p))
      if (p.messages.length) setMessages((prev) => merge(prev, after ? p.messages : [...p.messages].reverse()))
    } catch {
      // Transient: the next tick or focus retries.
    } finally { pollingRef.current = false }
  }, [url])

  useEffect(() => { if (refreshToken) void poll() }, [refreshToken, poll])

  useEffect(() => {
    if (!inProgress && !thread?.can_retry_sync && !awaitingReview) return
    const deadline = Date.now() + 60_000
    let active = true
    let timer: number | undefined
    const loop = async () => {
      await poll(inProgress)
      if (active && (inProgress || awaitingReview || Date.now() < deadline)) timer = window.setTimeout(() => { void loop() }, inProgress ? POLL_MS : 2000)
    }
    timer = window.setTimeout(() => { void loop() }, POLL_MS)
    return () => { active = false; if (timer !== undefined) window.clearTimeout(timer) }
  }, [inProgress, thread?.can_retry_sync, awaitingReview, retrySync, poll])

  useEffect(() => {
    // Coming back to the tab: catch up, or retry a first load that failed.
    const onFocus = () => { if (readyRef.current) void poll(); else if (loadError) setReloadKey((k) => k + 1) }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [poll, loadError])

  // Keep the newest message in view; keep the reader's place when older messages are prepended.
  useLayoutEffect(() => {
    const el = logRef.current
    if (!el) return
    if (prependRef.current !== null) { el.scrollTop += el.scrollHeight - prependRef.current; prependRef.current = null }
    else el.scrollTop = el.scrollHeight
  }, [lastSeq, visible.length])

  // The panel is `hidden` until its tab is chosen: scroll to the newest message when the log first gets a size.
  useEffect(() => {
    const el = logRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    let wasVisible = el.clientHeight > 0
    const observer = new ResizeObserver(() => {
      const isVisible = el.clientHeight > 0
      if (isVisible && !wasVisible) el.scrollTop = el.scrollHeight
      wasVisible = isVisible
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible.length > 0])

  async function loadOlder() {
    if (!cursor || loadingOlder) return
    setLoadingOlder(true); setActionError(null)
    try {
      const p = await api.get<CoachPage>(url, { before: cursor, limit: PAGE_SIZE })
      prependRef.current = logRef.current?.scrollHeight ?? null
      setMessages((prev) => merge(prev, p.messages)); setCursor(p.cursor)
    } catch (e) { setActionError((e as Error).message) }
    finally { setLoadingOlder(false) }
  }

  async function send(retry?: CoachSend) {
    const message = (retry?.message ?? text).trim()
    if (exampleMessages || !message || !page?.permissions.can_chat || inProgress || sending || (!retry && uploadsBlocked)) return
    const body: CoachSend = retry ? { ...retry } : { message, attachment_ids: attachments.map(f => f.file_id) }
    if (!retry && draft) { const d = cleanDraft(draft()); if (d) body.draft = d }
    if (!body.request_id) {
      const signature = JSON.stringify(body)
      if (lastSend.current?.signature !== signature) lastSend.current = { signature, id: crypto.randomUUID() }
      body.request_id = lastSend.current.id
    }
    const errs = validate('coach_message', body)
    if (errs.length) { setSendError(errs.map((e) => e.message).join(' ')); return }
    setSending(true); setSendError(null)
    try {
      const res = await postWhenCoachReady<{ thread: CoachThread; messages: CoachMessage[] }>(
        conversationUrl ? `${conversationUrl}/messages` : `/sme/tasks/${taskId}/coach/messages`,
        body,
        { onProgress: setProgress },
      )
      setPage((prev) => (prev ? { ...prev, thread: res.thread, pending_send: null } : prev))
      setMessages((prev) => merge(prev, res.messages))
      setText(''); setAttachments([]); setUploadsBlocked(false); setAttachmentKey(k => k + 1); lastSend.current = undefined
    } catch (e) {
      if (e instanceof ApiError && e.status === 503) setSendError(e.message || UNAVAILABLE)
      else if (e instanceof ApiError && e.status === 409) { setSendError(e.message); void poll() }
      else setSendError((e as Error).message)
      void poll()
    } finally { setSending(false); setProgress(null) }
  }

  async function download() {
    setActionBusy('download'); setActionError(null)
    try {
      const markdown = await api.get<string>(`${url}.md`)
      const blobUrl = URL.createObjectURL(new Blob([typeof markdown === 'string' ? markdown : JSON.stringify(markdown)], { type: 'text/markdown;charset=utf-8' }))
      const a = document.createElement('a')
      a.href = blobUrl; a.download = `${taskId}-coach.md`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    } catch (e) { setActionError((e as Error).message) }
    finally { setActionBusy(null) }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send() }
  }
  function onSubmit(e: FormEvent) { e.preventDefault(); void send() }
  function pickExample(q: string) { setText(q); textareaRef.current?.focus() }

  if (page && !page.enabled) return engineer ? null : <p className="muted small coach-off">{UNAVAILABLE}</p>
  if (loadError) return <div className="note err coach-load-error">{loadError} <button type="button" className="btn ghost small" onClick={() => setReloadKey((k) => k + 1)}>Retry</button></div>
  if (!page) return <p className="muted small">Loading…</p>

  if (awaitingReview && !page.thread?.message_count) return null

  const canChat = page.permissions.can_chat && !engineer && !awaitingReview && !readOnly
  const lastVisible = visible[visible.length - 1]
  const activity = inProgress ? (thread?.current_step ?? (lastVisible?.role === 'activity' ? lastVisible.text : null)) : null

  const status = inProgress
    ? <div className="coach-status busy" role="status">{activity ? `${activity}…` : 'The coach is working…'}</div>
    : thread?.status === 'stalled'
      ? <div className="coach-status" role="status">The coach seems stuck. {canChat ? 'You can send a new message.' : 'The expert can send a new message.'}</div>
      : thread?.status === 'failed'
        ? <div className="note err coach-status-note" role="status">{awaitingReview ? 'The draft review did not finish.' : <>The coach’s last reply is unavailable. {canChat ? 'You can send a new message.' : 'The expert can send a new message.'}</>}</div>
        : null

  const body = (
    <div className={`coach ${compact ? 'compact' : ''}`}>
      {cursor && <div className="row"><button type="button" className="sm" disabled={loadingOlder} onClick={() => void loadOlder()}>{loadingOlder ? 'Loading…' : 'Load earlier messages'}</button></div>}
      {visible.length === 0 && (readOnly
        ? <p className="muted small">No conversation was started for this review.</p>
        : engineer
        ? <p className="muted small">No conversation yet. The expert has not asked the coach anything about this work.</p>
        : canChat && showIntro && <Intro onPick={pickExample} />)}
      {visible.length > 0 && (
        <div className="coach-log" ref={logRef} role="log" aria-live="polite" aria-label="Conversation with the coach">
          {visible.map((m) => <CoachMessageView conversationUrl={conversationUrl} key={m.seq} m={m} viewer={role} taskId={taskId} />)}
        </div>
      )}
      {status}
      {thread?.can_retry_sync && <button type="button" className="ghost small" onClick={() => { setRetrySync(n => n + 1); void poll() }}>Retry fetching reply</button>}
      {canChat && page.pending_send && <div className="note" role="status">Your previous message still needs to finish sending.<p className="prewrap">{page.pending_send.data.message}</p><button type="button" className="ghost small" disabled={sending} onClick={() => void send(page.pending_send!.data)}>Retry sending</button></div>}
      {actionError && <div className="field-error" role="alert">{actionError}</div>}
      {canChat && (
        <form className="coach-compose" onSubmit={onSubmit}>
          <CoachAttachments baseUrl={conversationUrl} key={`${taskId}-${attachmentKey}`} taskId={taskId} disabled={!!exampleMessages || inProgress || sending || !!page.pending_send}
            onChange={(files, blocked) => { setAttachments(files); setUploadsBlocked(blocked) }}
            actions={<>
              {text.length >= MAX_CHARS * .9 && <span className="coach-counter small muted">{text.length}/{MAX_CHARS}</span>}
              <MessageSendButton busy={sending}
                disabled={!!exampleMessages || inProgress || sending || uploadsBlocked || !!page.pending_send || !text.trim()} />
            </>}>
            <label className="field">
              <span className="visually-hidden">Your message</span>
              <textarea ref={textareaRef} value={text} maxLength={MAX_CHARS} rows={compact ? 3 : 4} disabled={!!exampleMessages || inProgress || sending || !!page.pending_send}
                onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}
                placeholder={draft ? 'Ask about the draft you are editing; the unsaved text goes along with your question.' : 'Ask about a criterion, or paste one and ask what could go wrong.'} />
            </label>
          </CoachAttachments>
          {sending && <p className="small muted" role="status">{progress ? coachProgressLabel[progress] : 'Sending…'}</p>}
          {sendError && <div className="field-error" role="alert">{sendError}</div>}
        </form>
      )}
    </div>
  )

  if (compact) return body
  const aside = engineer && page.permissions.can_export
    ? <button type="button" className="sm" disabled={actionBusy === 'download'} onClick={() => void download()} title="Download the whole conversation as Markdown">{actionBusy === 'download' ? 'Downloading…' : 'Download conversation (.md)'}</button>
    : undefined
  return (
    <Section title="Coach" aside={aside}
      lead={engineer ? 'The expert’s conversation with the coach, read-only.' : undefined}>
      {body}
    </Section>
  )
}

function Intro({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div className="coach-intro">
      <p><strong>The coach helps you sharpen your rubric.</strong> It asks questions, points out criteria a verifier could not check or a model could game, and challenges vague wording. It never writes the rubric: the criteria stay yours.</p>
      <p>Engineers can read this conversation.</p>
      <div className="coach-examples" role="group" aria-label="Example questions">
        {EXAMPLES.map((q) => <button key={q} type="button" onClick={() => onPick(q)}>{q}</button>)}
      </div>
    </div>
  )
}

/** Saved and example messages share presentation without starting a conversation. */
export function CoachMessageView({ m, viewer, taskId, conversationUrl }: { m: CoachMessage; viewer: Role; taskId: string; conversationUrl?: string }) {
  switch (m.role) {
    case 'expert':
      return <div className="coach-msg expert"><div className="coach-bubble prewrap">{m.text}{m.attachments?.length ? <div className="coach-message-files">{m.attachments.map(file => <CoachAttachmentLink baseUrl={conversationUrl} key={file.file_id} taskId={taskId} file={file} />)}</div> : null}</div><div className="coach-meta">{viewer === 'sme' ? 'You' : 'Expert'} · {fmtDate(m.at)}{m.draft_included && ' · with the unsaved draft'}</div></div>
    case 'coach':
      return <div className="coach-msg coach"><div className="coach-bubble"><Markdown source={m.text} /></div><div className="coach-meta">Coach · {fmtDate(m.at)}</div></div>
    case 'coach_note':
      return <div className="coach-note">{m.text}</div>
    case 'activity':
      return <div className="coach-activity">{m.text}</div>
    default:
      return <div className="coach-note">{m.text}</div>
  }
}
